import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { SxProps, Theme } from '@mui/material/styles';
import { TimePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';

import Label from './Label';

type Props<T extends FieldValues> = UseControllerProps<T> & {
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  manualError?: string;
  suppressErrorMessage?: boolean;
  sx?: SxProps<Theme>;
};

export default function TimeInput<T extends FieldValues>({
  control,
  isDisabled,
  isRequired,
  label,
  manualError,
  name,
  suppressErrorMessage,
  sx,
}: Props<T>) {
  const { field, fieldState } = useController({ control, name });
  const error =
    manualError ?? fieldState.error?.message ?? fieldState.error?.root?.message;

  const value = useMemo(() => {
    if (isNil(field.value)) {
      return null;
    }

    try {
      const value = dayjs.tz(field.value);

      if (value.isValid()) {
        return value;
      }

      return null;
    } catch {
      return null;
    }
  }, [field.value]);

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <TimePicker
        closeOnSelect={false}
        enableAccessibleFieldDOMStructure={false}
        disabled={isDisabled}
        onChange={field.onChange}
        value={value}
        slotProps={{
          actionBar: desktopActionBar(['accept']),
          textField: {
            className: isRequired ? 'required' : '',
            error: Boolean(error),
            fullWidth: true,
            slotProps: { htmlInput: { sx: { fontSize: '12px', height: '23px' } } },
            required: isRequired,
            variant: 'outlined',
            sx: { ...sx, [`& .${inputAdornmentClasses.root}`]: { ml: 0 } },
          },
        }}
      />

      {error && !suppressErrorMessage && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
